import { PlusOutlined } from '@ant-design/icons';
import {
  COUNTERPARTY_KIND_LABELS,
  type CounterpartyKind,
  categoriesFor,
  matchesWords,
  type OperationType,
  present,
} from '@horof/shared';
import { Button, Select } from 'antd';
import { type ReactNode, useState } from 'react';
import { useLists } from './data';
import { CategoryDialog, CounterpartyDialog, ProjectDialog } from './dialogs';

type Common = {
  id?: string;
  size?: 'small' | 'middle';
  /** 'borderless' inside a table cell. */
  variant?: 'borderless';
  disabled?: boolean;
  placeholder?: string;
  /** Highlight a changed, not yet saved value (table cells). */
  changed?: boolean;
};

/**
 * A searchable select whose list ends with «إضافة «…»» for a name that isn't
 * there. Adding opens a dialog (nothing is created until «حفظ»), and the new
 * item is selected.
 */
function CreatableSelect({
  id,
  size,
  variant,
  disabled,
  placeholder,
  changed,
  value,
  onChange,
  options,
  allowClear = true,
  addLabel,
  dialog,
}: Common & {
  value: string | null;
  onChange: (value: string | null) => void;
  options: { value: string; label: string; hint?: string }[];
  allowClear?: boolean;
  addLabel: (name: string) => string;
  /** The dialog for a new item, prefilled with the typed name. */
  dialog: (name: string, done: (id: string | null) => void) => ReactNode;
}) {
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const name = search.trim();
  const exists = options.some((o) => o.label === name);

  return (
    <>
      <Select
        id={id}
        size={size}
        variant={variant}
        disabled={disabled}
        className={changed ? 'is-changed' : undefined}
        showSearch
        allowClear={allowClear}
        placeholder={placeholder}
        value={value ?? undefined}
        onChange={(v) => onChange(v ?? null)}
        searchValue={search}
        onSearch={setSearch}
        filterOption={(input, option) =>
          matchesWords([option?.label as string, option?.hint], input)
        }
        options={options}
        optionRender={(option) =>
          option.data.hint ? (
            <span className="desk-option">
              {option.label}
              <span className="field-hint">{option.data.hint}</span>
            </span>
          ) : (
            option.label
          )
        }
        // Something to show, or antd keeps the list (and «إضافة») closed.
        notFoundContent={<span className="field-hint">لا نتيجة</span>}
        popupMatchSelectWidth={260}
        popupRender={(menu) => (
          <>
            {menu}
            <Button
              type="link"
              block
              icon={<PlusOutlined />}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setAdding(exists ? '' : name)}
            >
              {name && !exists ? addLabel(name) : addLabel('')}
            </Button>
          </>
        )}
      />
      {adding !== null &&
        dialog(adding, (created) => {
          setAdding(null);
          setSearch('');
          if (created) onChange(created);
        })}
    </>
  );
}

export function CategoryPicker({
  type,
  value,
  onChange,
  ...common
}: Common & {
  type: OperationType | null;
  value: string | null;
  onChange: (categoryId: string | null) => void;
}) {
  const [{ data }] = useLists();
  return (
    <CreatableSelect
      {...common}
      value={value}
      onChange={onChange}
      options={categoriesFor(
        present(data?.operationCategories?.nodes),
        type,
      ).map((c) => ({ value: c.id, label: c.name }))}
      addLabel={(name) => (name ? `إضافة صنف «${name}»` : 'صنف جديد')}
      dialog={(name, done) => (
        <CategoryDialog
          category={{ id: null, name, appliesTo: type ?? 'both' }}
          onClose={done}
        />
      )}
    />
  );
}

export function ProjectPicker({
  value,
  onChange,
  keep,
  clearable = false,
  ...common
}: Common & {
  value: string | null;
  onChange: (projectId: string | null) => void;
  /** A closed project still shown (the operation's own). */
  keep?: string | null;
  /** Optional (loans, in-kind); an operation always has one. */
  clearable?: boolean;
}) {
  const [{ data }] = useLists();
  return (
    <CreatableSelect
      {...common}
      allowClear={clearable}
      value={value}
      onChange={onChange}
      options={present(data?.projects?.nodes)
        .filter((p) => p.status === 'OPEN' || p.id === keep)
        .map((p) => ({ value: p.id, label: p.name }))}
      addLabel={(name) => (name ? `إضافة مشروع «${name}»` : 'مشروع جديد')}
      dialog={(name, done) => (
        <ProjectDialog
          project={{ id: null, name, description: '' }}
          onClose={done}
        />
      )}
    />
  );
}

export function CounterpartyPicker({
  type,
  value,
  onChange,
  kind,
  ...common
}: Common & {
  type: OperationType | null;
  value: string | null;
  onChange: (counterpartyId: string | null) => void;
  /** The kind proposed for a new one (else: donor for income, supplier). */
  kind?: CounterpartyKind;
}) {
  const [{ data }] = useLists();
  return (
    <CreatableSelect
      {...common}
      value={value}
      onChange={onChange}
      options={present(data?.counterparties?.nodes).map((c) => ({
        value: c.id,
        label: c.name,
        hint: COUNTERPARTY_KIND_LABELS[c.kind],
      }))}
      addLabel={(name) => (name ? `إضافة جهة «${name}»` : 'جهة جديدة')}
      dialog={(name, done) => (
        <CounterpartyDialog
          counterparty={{
            id: null,
            name,
            kind: kind ?? (type === 'credit' ? 'donor' : 'supplier'),
            phone: '',
            note: '',
          }}
          onClose={done}
        />
      )}
    />
  );
}
